import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Filter, ChevronDown, ChevronUp } from "lucide-react";

const REASON_LABELS = {
  skip_trend_filter: "200-EMA trend filter",
  skip_volume_filter: "Volume filter",
  skip_mtf_filter: "Daily MACD (MTF) filter",
  skip_spy_trend_filter: "SPY market-trend filter",
  skip_hist_slope_filter: "Histogram slope filter",
  skip_threshold_or_holding: "Below MACD threshold / already holding",
  skip_short_disabled: "Shorts disabled",
  skip_order_already_pending: "Order already pending",
  skip_insufficient_cash: "Insufficient cash",
};

function reasonLabel(key) {
  if (REASON_LABELS[key]) return REASON_LABELS[key];
  if (key.startsWith("skip_cooldown")) return `Re-entry cooldown (${key.replace("skip_cooldown_", "")})`;
  if (key.startsWith("skip_max_positions")) return `Max positions cap (${key.replace("skip_max_positions_", "")})`;
  if (key.startsWith("skip_rsi_overbought")) return `RSI overbought (${key.replace("skip_rsi_overbought_", "")})`;
  if (key.startsWith("skip_rsi_oversold")) return `RSI oversold (${key.replace("skip_rsi_oversold_", "")})`;
  if (key.startsWith("skip_rsi_no_momentum")) return `RSI momentum too low (${key.replace("skip_rsi_no_momentum_", "")})`;
  if (key.startsWith("skip_rsi_no_weakness")) return `RSI not weak enough (${key.replace("skip_rsi_no_weakness_", "")})`;
  if (key.startsWith("skip_buy_macd") || key.startsWith("skip_sell_macd")) return "MACD below threshold";
  return key.replace(/^skip_/, "").replace(/_/g, " ");
}

function timeStr(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export const ScanFunnel = () => {
  const [scans, setScans] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/strategy/scan-funnel?limit=10");
      setScans(r.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  if (scans.length === 0) return null;

  const latest = scans[0];
  const c = latest.counts || {};
  const rejections = latest.rejections || {};
  const executed = latest.executed || [];
  const crossovers = latest.crossovers || [];

  return (
    <div className="panel" data-testid="scan-funnel-panel">
      <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
          <Filter size={13} /> SCAN FUNNEL · {timeStr(latest.timestamp)} · {latest.bar_timeframe}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid="scan-funnel-toggle"
          className="mono text-[10px] text-[#71717A] hover:text-white flex items-center gap-1"
        >
          {expanded ? <>HIDE HISTORY <ChevronUp size={11} /></> : <>SCAN HISTORY <ChevronDown size={11} /></>}
        </button>
      </div>

      {/* Latest scan breakdown */}
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2 mono text-[10px]" data-testid="scan-funnel-counts">
          <span className="border border-[#27272A] px-2 py-1 text-white">{c.scanned ?? 0} SCANNED</span>
          <span className="border border-[#27272A] px-2 py-1 text-[#71717A]">{c.no_crossover ?? 0} NO CROSSOVER</span>
          {(c.stale_bar ?? 0) > 0 && (
            <span className="border border-[#EAB308]/40 text-[#EAB308] px-2 py-1">{c.stale_bar} STALE BAR</span>
          )}
          {(c.no_data ?? 0) > 0 && (
            <span className="border border-[#FF3B30]/40 text-[#FF3B30] px-2 py-1">{c.no_data} NO DATA</span>
          )}
          <span className={`border px-2 py-1 ${(c.crossovers ?? 0) > 0 ? "border-white text-white font-bold" : "border-[#27272A] text-[#71717A]"}`}>
            {c.crossovers ?? 0} CROSSOVERS
          </span>
          {(c.rejected ?? 0) > 0 && (
            <span className="border border-[#FF3B30]/40 text-[#FF3B30] px-2 py-1">{c.rejected} REJECTED</span>
          )}
          <span className={`border px-2 py-1 ${(c.executed ?? 0) > 0 ? "border-[#00E676] text-[#00E676] font-bold" : "border-[#27272A] text-[#71717A]"}`}>
            {c.executed ?? 0} EXECUTED
          </span>
        </div>

        {Object.keys(rejections).length > 0 && (
          <div className="space-y-1" data-testid="scan-funnel-rejections">
            {Object.entries(rejections).map(([reason, syms]) => (
              <div key={reason} className="mono text-[11px] flex items-start gap-2">
                <span className="text-[#FF3B30] shrink-0">✕ {reasonLabel(reason)}:</span>
                <span className="text-[#A1A1AA]">{syms.join(", ")}</span>
              </div>
            ))}
          </div>
        )}

        {executed.length > 0 && (
          <div className="space-y-1" data-testid="scan-funnel-executed">
            {executed.map((e, i) => (
              <div key={i} className="mono text-[11px]">
                <span className="text-[#00E676]">✓ {e.symbol}</span>
                <span className="text-[#A1A1AA]"> — {e.action}</span>
              </div>
            ))}
          </div>
        )}

        {crossovers.length === 0 && (
          <div className="mono text-[11px] text-[#71717A]">
            No MACD crossovers this scan — nothing to filter. If this persists all day, the market simply isn't crossing on your timeframe.
          </div>
        )}
      </div>

      {/* Scan history */}
      {expanded && (
        <div className="border-t border-[#27272A] max-h-56 overflow-y-auto" data-testid="scan-funnel-history">
          <table className="w-full text-sm">
            <thead className="bg-[#141415] sticky top-0">
              <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                <th className="text-left px-4 py-2 font-normal">TIME</th>
                <th className="text-right px-3 py-2 font-normal">SCANNED</th>
                <th className="text-right px-3 py-2 font-normal">CROSSOVERS</th>
                <th className="text-right px-3 py-2 font-normal">REJECTED</th>
                <th className="text-right px-3 py-2 font-normal">STALE</th>
                <th className="text-right px-4 py-2 font-normal">EXECUTED</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s, i) => {
                const sc = s.counts || {};
                return (
                  <tr key={i} className="border-b border-[#27272A]">
                    <td className="px-4 py-1.5 mono text-xs text-[#A1A1AA]">{timeStr(s.timestamp)}</td>
                    <td className="px-3 py-1.5 mono text-xs text-right">{sc.scanned ?? 0}</td>
                    <td className={`px-3 py-1.5 mono text-xs text-right ${(sc.crossovers ?? 0) > 0 ? "text-white" : "text-[#71717A]"}`}>{sc.crossovers ?? 0}</td>
                    <td className={`px-3 py-1.5 mono text-xs text-right ${(sc.rejected ?? 0) > 0 ? "loss" : "text-[#71717A]"}`}>{sc.rejected ?? 0}</td>
                    <td className="px-3 py-1.5 mono text-xs text-right text-[#71717A]">{sc.stale_bar ?? 0}</td>
                    <td className={`px-4 py-1.5 mono text-xs text-right ${(sc.executed ?? 0) > 0 ? "gain" : "text-[#71717A]"}`}>{sc.executed ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScanFunnel;
